import React, { useState, useEffect } from 'react';
import BBALoader from '../components/BBALoader';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { FaArrowRight, FaArrowLeft, FaCheck, FaBuilding, FaProjectDiagram, FaGlobe, FaBalanceScale, FaStar } from 'react-icons/fa';
import StartSidebar from '../components/StartSidebar';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { sanitizeAlaCarteForPublic } from '../utils/alaCartePublic';
import { isRetiredProgram } from '../utils/retiredPrograms';

// Simple Team Member Card Component
const TeamMemberCard = ({ name, fullName, avatarUrl, borderColor, services }) => {
  const [isFlipped, setIsFlipped] = useState(false);

  return (
    <div 
      className={`border-2 ${borderColor} rounded-xl overflow-hidden cursor-pointer transition-all hover:shadow-lg`}
      onClick={() => setIsFlipped(!isFlipped)}
    >
      {!isFlipped ? (
        // Front Side - Profile
        <div className="bg-white/20 dark:bg-white/10 backdrop-blur-md p-6 flex flex-col items-center h-full min-h-[400px]">
          <h3 className="text-2xl font-bold text-gray-700 dark:text-gray-300 mb-2 text-center">
            {name}
          </h3>
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-4 text-center">
            {fullName}
          </p>
          <div className="flex-1 flex items-center justify-center w-full">
            <img 
              src={avatarUrl} 
              alt={fullName}
              className="w-full h-auto object-cover rounded-lg"
            />
          </div>
        </div>
      ) : (
        // Back Side - Services
        <div className="bg-white/20 dark:bg-white/10 backdrop-blur-md p-6 h-full min-h-[400px] flex flex-col">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white">{name}</h3>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsFlipped(false);
              }}
              className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors text-sm font-semibold"
            >
              ← Back
            </button>
          </div>
          <div className="flex-1">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">What We Cover</h4>
            <div className="space-y-3">
              {services.map((service, index) => (
                <div
                  key={index}
                  className="bg-white/20 dark:bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/30 dark:border-white/20 hover:bg-white/30 dark:hover:bg-white/20 transition-colors"
                >
                  <p className="text-gray-900 dark:text-white font-medium">{service}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const StartAssessment = () => {
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  // State to track completed steps (1-7: Company Snapshot, BBA Team, Establish Why, Existing Conditions, Proposed Conditions, Choose Program, Sign Up)
  const [completedSteps, setCompletedSteps] = useState(new Set());
  
  // State to control stepper step
  const [stepperStep, setStepperStep] = useState(1);
  
  // State to store company name
  const [companyName, setCompanyName] = useState(null);
  
  // State for Company Snapshot inputs
  const [companyYears, setCompanyYears] = useState('');
  const [teamSize, setTeamSize] = useState('');
  const [primaryProjects, setPrimaryProjects] = useState('');
  const [averageSize, setAverageSize] = useState('');
  const [projectType, setProjectType] = useState('');
  
  // State for programs
  const [programs, setPrograms] = useState({});
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [billingCycles, setBillingCycles] = useState({});
  const [isLoadingPrograms, setIsLoadingPrograms] = useState(true);
  const [availableItems, setAvailableItems] = useState([]);
  const [showcaseOrder, setShowcaseOrder] = useState({});
  const [selectedProgramFilter, setSelectedProgramFilter] = useState('Lite');
  const [suggestedProgram, setSuggestedProgram] = useState(null);
  
  // Get current step number for stepper
  const getCurrentStepNumber = () => {
    for (let i = 1; i <= 7; i++) {
      if (!completedSteps.has(i)) {
        return i;
      }
    }
    return 7; // All steps completed, show last step
  };
  
  // Update stepper step when current step changes
  useEffect(() => {
    const newStep = getCurrentStepNumber();
    setStepperStep(newStep);
  }, [completedSteps]);

  // Function to get the current step (first incomplete step)
  const getCurrentStep = () => {
    for (let i = 1; i <= 7; i++) {
      if (!completedSteps.has(i)) {
        return i;
      }
    }
    return 7; // All steps completed, show last step
  };

  // Function to check if a step is accessible
  const isStepAccessible = (stepNumber) => {
    const currentStep = getCurrentStep();
    // Only the current step (next incomplete step) is accessible
    return currentStep === stepNumber;
  };
  
  // Function to check if a step is completed
  const isStepCompleted = (stepNumber) => {
    return completedSteps.has(stepNumber);
  };

  // Function to navigate to previous step
  const goToPreviousStep = () => {
    if (stepperStep > 1) {
      const previousStep = stepperStep - 1;
      const canAccess = isStepAccessible(previousStep) || isStepCompleted(previousStep);
      if (canAccess) {
        setStepperStep(previousStep);
      }
    }
  };

  // Function to navigate to next step
  const goToNextStep = () => {
    if (stepperStep < 7) {
      const nextStep = stepperStep + 1;
      const canAccess = isStepAccessible(nextStep) || isStepCompleted(nextStep);
      if (canAccess) {
        setStepperStep(nextStep);
      }
    }
  };
  
  // Load step completion state and company name from Firebase
  useEffect(() => {
    const loadStepProgress = async () => {
      if (currentUser) {
        try {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            console.log('Loading user data from Firebase:', userData.startFlowProgress);
            
            // Load company name from user data first
            if (userData.company) {
              setCompanyName(userData.company);
            }
            
            // Try to get company name from account
            let accountId = userData.accountId;
            
            // If no accountId, try to find account by email
            if (!accountId && currentUser.email) {
              try {
                const normalizedEmail = currentUser.email.toLowerCase().trim();
                
                // Try multiple strategies to find the account
                const searchStrategies = [
                  query(
                    collection(db, 'accounts'),
                    where('contact.email', '==', normalizedEmail),
                    where('type', '==', 'Leads')
                  ),
                  query(
                    collection(db, 'accounts'),
                    where('email', '==', normalizedEmail),
                    where('type', '==', 'Leads')
                  ),
                  query(
                    collection(db, 'accounts'),
                    where('contact.email', '==', normalizedEmail)
                  ),
                  query(
                    collection(db, 'accounts'),
                    where('email', '==', normalizedEmail)
                  )
                ];
                
                for (const searchQuery of searchStrategies) {
                  try {
                    const accountsSnapshot = await getDocs(searchQuery);
                    if (!accountsSnapshot.empty) {
                      accountId = accountsSnapshot.docs[0].id;
                      break;
                    }
                  } catch (queryError) {
                    console.warn('Query strategy failed:', queryError);
                  }
                }
              } catch (error) {
                console.error('Error finding account by email:', error);
              }
            }
            
            // Load company name from account if we have accountId
            if (accountId && !companyName) {
              try {
                const accountDoc = await getDoc(doc(db, 'accounts', accountId));
                if (accountDoc.exists()) {
                  const accountData = accountDoc.data();
                  if (accountData.company) {
                    setCompanyName(accountData.company);
                  }
                }
              } catch (error) {
                console.error('Error loading account data:', error);
              }
            }
            
            if (userData.startFlowProgress) {
              // If contract is signed, all steps should be completed
              if (userData.startFlowProgress.contractSigned) {
                console.log('Contract is signed - setting all steps as completed');
                setCompletedSteps(new Set([1, 2, 3, 4, 5, 6, 7]));
              } else {
                // Only load individual step data if contract is not signed
                if (userData.startFlowProgress.completedSteps) {
                  setCompletedSteps(new Set(userData.startFlowProgress.completedSteps));
                  console.log('Loaded completedSteps from Firebase:', userData.startFlowProgress.completedSteps);
                }
              }
            }
          }
        } catch (error) {
          console.error('Error loading step progress:', error);
        }
      }
    };

    loadStepProgress();
  }, [currentUser]);

  // Load programs and billing cycles
  useEffect(() => {
    const loadPrograms = async () => {
      try {
        setIsLoadingPrograms(true);
        
        // Load programs data
        const programsSnapshot = await getDocs(collection(db, 'programs'));
        if (!programsSnapshot.empty) {
          const programsData = {};
          programsSnapshot.forEach(doc => {
            const programData = doc.data();
            if (programData && typeof programData === 'object') {
              if (!programData.features || typeof programData.features !== 'object') {
                programData.features = {};
              }
              if (!programData.name) programData.name = doc.id;
              if (!programData.description) programData.description = 'Program description';
              if (!programData.pricePerMonth) programData.pricePerMonth = 0;
              if (typeof programData.isMostPopular !== 'boolean') programData.isMostPopular = false;
              
              programsData[doc.id] = programData;
            }
          });
          setPrograms(programsData);
        }

        // Load billing cycles data
        const billingSnapshot = await getDocs(collection(db, 'billingCycles'));
        if (!billingSnapshot.empty) {
          const billingData = {};
          billingSnapshot.forEach(doc => {
            billingData[doc.id] = doc.data();
          });
          setBillingCycles(billingData);
          
          // Set first billing cycle as default
          if (Object.keys(billingData).length > 0) {
            setBillingCycle(Object.keys(billingData)[0]);
          }
        }

        // Load available items from alaCarteItems collection
        const itemsSnapshot = await getDocs(collection(db, 'alaCarteItems'));
        const itemsData = itemsSnapshot.docs.map((docSnap) =>
          sanitizeAlaCarteForPublic({ id: docSnap.id, ...docSnap.data() })
        );
        setAvailableItems(itemsData);

        // Load showcase order from settings collection
        const settingsSnapshot = await getDocs(collection(db, 'settings'));
        const showcaseOrderDoc = settingsSnapshot.docs.find(doc => doc.id === 'showcaseOrder');
        if (showcaseOrderDoc) {
          setShowcaseOrder(showcaseOrderDoc.data().order || {});
        }
      } catch (error) {
        console.error('Error loading programs:', error);
      } finally {
        setIsLoadingPrograms(false);
      }
    };

    loadPrograms();
  }, []);

  // Helper functions for pricing
  const getAnnualPrice = (pricePerMonth) => {
    if (billingCycle === 'annual' && billingCycles?.annual) {
      return Math.round(pricePerMonth * billingCycles.annual.months * (1 - billingCycles.annual.discount / 100));
    } else if (billingCycle === 'quarterly' && billingCycles?.quarterly) {
      return Math.round(pricePerMonth * billingCycles.quarterly.months * (1 - billingCycles.quarterly.discount / 100));
    }
    return pricePerMonth * 12;
  };

  const getMonthlyPrice = (pricePerMonth) => {
    if (billingCycle === 'annual' && billingCycles?.annual) {
      return Math.round(pricePerMonth * (1 - billingCycles.annual.discount / 100));
    } else if (billingCycle === 'quarterly' && billingCycles?.quarterly) {
      return Math.round(pricePerMonth * (1 - billingCycles.quarterly.discount / 100));
    }
    return pricePerMonth;
  };

  const getQuarterlyPrice = (pricePerMonth) => {
    if (billingCycle === 'quarterly' && billingCycles?.quarterly) {
      return Math.round(pricePerMonth * billingCycles.quarterly.months * (1 - billingCycles.quarterly.discount / 100));
    }
    return pricePerMonth * 3;
  };

  // Get ordered programs (lite, plus, pro, bba bundle)
  const getOrderedPrograms = () => {
    if (!programs || Object.keys(programs).length === 0) return [];
    
    const getProgramOrder = (programKey, programName) => {
      const keyLower = programKey.toLowerCase().trim();
      const nameLower = (programName || '').toLowerCase().trim();
      
      if (keyLower === 'lite' || nameLower === 'lite' || keyLower.includes('lite') || nameLower.includes('lite')) {
        return 1;
      }
      if (keyLower === 'plus' || nameLower === 'plus' || keyLower.includes('plus') || nameLower.includes('plus')) {
        return 2;
      }
      if ((keyLower === 'pro' || nameLower === 'pro' || keyLower.includes('pro') || nameLower.includes('pro')) &&
          !keyLower.includes('plus') && !nameLower.includes('plus')) {
        return 3;
      }
      if (keyLower.includes('bba') || nameLower.includes('bba') ||
          keyLower === 'bba bundle' || keyLower === 'bbabundle' || keyLower === 'bba-bundle') {
        return 4;
      }
      return 999;
    };
    
    return Object.entries(programs).filter(([key, program]) => !isRetiredProgram(key, program)).sort(([keyA, programA], [keyB, programB]) => {
      const nameA = programA?.name || '';
      const nameB = programB?.name || '';
      const orderA = getProgramOrder(keyA, nameA);
      const orderB = getProgramOrder(keyB, nameB);
      return orderA - orderB;
    });
  };

  // Validate program data
  const validateProgram = (program) => {
    if (!program || typeof program !== 'object') {
      return {
        name: 'Unknown Program',
        description: 'Program description',
        pricePerMonth: 0,
        isMostPopular: false,
        minimumTerm: 12,
        features: {}
      };
    }
    
    return {
      name: program.name || 'Unknown Program',
      description: program.description || 'Program description',
      pricePerMonth: program.pricePerMonth || 0,
      isMostPopular: program.isMostPopular || false,
      minimumTerm: program.minimumTerm || 12,
      features: program.features && typeof program.features === 'object' ? program.features : {}
    };
  };

  // Get ordered showcase features
  const getOrderedShowcaseFeatures = (programKey) => {
    const program = programs[programKey];
    if (!program || !program.features) return [];
    
    const showcaseFeatures = Object.entries(program.features)
      .filter(([_, featureData]) => featureData.showcase)
      .map(([featureName, _]) => featureName);
    
    const savedOrder = showcaseOrder[programKey] || [];
    
    if (savedOrder.length === 0 && showcaseFeatures.length > 0) {
      return showcaseFeatures;
    }
    
    // Sort features according to saved order
    const orderedFeatures = [];
    savedOrder.forEach(featureName => {
      if (showcaseFeatures.includes(featureName)) {
        orderedFeatures.push(featureName);
      }
    });
    
    // Add any features not in saved order
    showcaseFeatures.forEach(featureName => {
      if (!orderedFeatures.includes(featureName)) {
        orderedFeatures.push(featureName);
      }
    });
    
    return orderedFeatures;
  };

  // Calendar data for each program
  const getCalendarEvents = (programKey) => {
    const calendars = {
      'Lite': [
        { day: 2, event: 'Financial Review', color: 'darkgreen' },
        { day: 3, event: 'Breakthrough Call', color: 'green' },
        { day: 5, event: 'Breakthrough Call', color: 'green' },
        { day: 10, event: 'Breakthrough Call', color: 'green' },
        { day: 11, event: 'Strategy Call', color: 'red' },
        { day: 12, event: 'Breakthrough Call', color: 'green' },
        { day: 16, event: 'Strategy Call', color: 'red' },
        { day: 17, event: 'Breakthrough Call', color: 'green' },
        { day: 19, event: 'Breakthrough Call', color: 'green' },
        { day: 24, event: 'Breakthrough Call', color: 'green' },
        { day: 26, event: 'Breakthrough Call', color: 'green' },
      ],
      'Plus': [
        { day: 2, event: 'Financial Review', color: 'darkgreen' },
        { day: 3, event: 'Breakthrough Call', color: 'green' },
        { day: 5, event: 'Breakthrough Call', color: 'green' },
        { day: 6, event: 'Strategy Call', color: 'red' },
        { day: 9, event: 'Strategy Call', color: 'red' },
        { day: 10, event: 'Breakthrough Call', color: 'green' },
        { day: 12, event: 'Breakthrough Call', color: 'green' },
        { day: 17, event: 'Breakthrough Call', color: 'green' },
        { day: 19, event: 'Breakthrough Call', color: 'green' },
        { day: 20, event: 'Strategy Call', color: 'red' },
        { day: 23, event: 'Strategy Call', color: 'red' },
        { day: 24, event: 'Breakthrough Call', color: 'green' },
        { day: 26, event: 'Breakthrough Call', color: 'green' },
      ],
      'Pro': [
        { day: 2, event: 'Financial Review', color: 'darkgreen' },
        { day: 3, event: 'Breakthrough Call', color: 'green' },
        { day: 4, event: 'Strategy Call', color: 'red' },
        { day: 5, event: 'Breakthrough Call', color: 'green' },
        { day: 6, event: 'Strategy Call', color: 'red' },
        { day: 9, event: 'Strategy Call', color: 'red' },
        { day: 10, event: 'Breakthrough Call', color: 'green' },
        { day: 11, event: 'Strategy Call', color: 'red' },
        { day: 12, event: 'CQC Visit', color: 'gold' },
        { day: 12, event: 'Breakthrough Call', color: 'green' },
        { day: 13, event: 'CQC Visit', color: 'gold' },
        { day: 16, event: 'Strategy Call', color: 'red' },
        { day: 17, event: 'Breakthrough Call', color: 'green' },
        { day: 19, event: 'Breakthrough Call', color: 'green' },
        { day: 20, event: 'Strategy Call', color: 'red' },
        { day: 23, event: 'Strategy Call', color: 'red' },
        { day: 24, event: 'Breakthrough Call', color: 'green' },
        { day: 26, event: 'Breakthrough Call', color: 'green' },
        { day: 27, event: 'Strategy Call', color: 'red' },
      ],
      'Bundle': [
        { day: 2, event: 'Financial Review', color: 'darkgreen' },
        { day: 2, event: 'Strategy Call', color: 'red' }, // Monday
        { day: 3, event: 'Breakthrough Call', color: 'green' },
        { day: 4, event: 'Strategy Call', color: 'red' }, // Wednesday
        { day: 5, event: 'Breakthrough Call', color: 'green' },
        { day: 6, event: 'Strategy Call', color: 'red' }, // Friday
        { day: 9, event: 'Strategy Call', color: 'red' }, // Monday
        { day: 10, event: 'Breakthrough Call', color: 'green' },
        { day: 11, event: 'Strategy Call', color: 'red' }, // Wednesday
        { day: 12, event: 'Breakthrough Call', color: 'green' },
        { day: 13, event: 'Strategy Call', color: 'red' }, // Friday
        { day: 16, event: 'Strategy Call', color: 'red' }, // Monday
        { day: 17, event: 'Breakthrough Call', color: 'green' },
        { day: 18, event: 'Strategy Call', color: 'red' }, // Wednesday
        { day: 19, event: 'On-Site Day 1', color: 'gold' },
        { day: 19, event: 'Breakthrough Call', color: 'green' },
        { day: 20, event: 'On-Site Day 2', color: 'gold' },
        { day: 20, event: 'Strategy Call', color: 'red' }, // Friday
        { day: 23, event: 'Strategy Call', color: 'red' }, // Monday
        { day: 24, event: 'Breakthrough Call', color: 'green' },
        { day: 25, event: 'Strategy Call', color: 'red' }, // Wednesday
        { day: 26, event: 'Breakthrough Call', color: 'green' },
        { day: 27, event: 'Strategy Call', color: 'red' }, // Friday
      ],
    };

    return calendars[programKey] || [];
  };

  // Get all events for "All" filter
  const getAllCalendarEvents = () => {
    const allEvents = [];
    ['Lite', 'Plus', 'Pro', 'Bundle'].forEach(programKey => {
      const events = getCalendarEvents(programKey);
      events.forEach(event => {
        if (!allEvents.find(e => e.day === event.day && e.event === event.event)) {
          allEvents.push({ ...event, program: programKey });
        }
      });
    });
    return allEvents.sort((a, b) => a.day - b.day);
  };

  // Get events for selected filter
  const getFilteredEvents = () => {
    if (selectedProgramFilter === 'All') {
      return getAllCalendarEvents();
    }
    
    // Map filter names to program keys
    const filterMap = {
      'Lite': 'Lite',
      'Plus': 'Plus',
      'Pro': 'Pro',
      'Bundle': 'Bundle'
    };
    
    const programKey = filterMap[selectedProgramFilter];
    return programKey ? getCalendarEvents(programKey) : [];
  };

  // Get color class for event - always use event-specific colors
  const getEventColorClass = (eventColor) => {
    const colorMap = {
      'green': 'bg-green-600',
      'red': 'bg-red-600',
      'purple': 'bg-purple-600',
      'orange': 'bg-orange-600',
      'blue': 'bg-blue-600',
      'gold': 'bg-yellow-400',
      'darkgreen': 'bg-green-800',
    };
    return colorMap[eventColor] || 'bg-gray-600';
  };

  // Render calendar
  const renderCalendar = () => {
    const events = getFilteredEvents();
    const daysInMonth = 28; // February 2026
    const firstDayOfWeek = 0; // February 1, 2026 is a Sunday
    
    const days = [];
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    
    // Add empty cells for days before the 1st
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dayEvents = events.filter(e => e.day === day);
      days.push({ day, events: dayEvents });
    }
    
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="text-center mb-4">
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white">February 2026</h3>
        </div>

        
        {/* Day names */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {dayNames.map(day => (
            <div key={day} className="text-center text-sm font-semibold text-gray-700 dark:text-gray-300 py-2">
              {day}
            </div>
          ))}
        </div>
        
        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1">
          {days.map((dayData, index) => {
            if (dayData === null) {
              return <div key={`empty-${index}`} className="aspect-square"></div>;
            }
            
            const { day, events: dayEvents } = dayData;
            
            return (
              <div
                key={day}
                className="aspect-square bg-gray-100 dark:bg-gray-700 rounded-lg p-1 flex flex-col items-center justify-start border border-gray-200 dark:border-gray-600"
              >
                <div className="text-sm font-medium text-white mb-1">{day}</div>
                <div className="w-full space-y-1 flex-1 overflow-y-auto">
                  {dayEvents.map((event, eventIndex) => (
                    <div
                      key={`${day}-${eventIndex}`}
                      className={`${getEventColorClass(event.color)} text-white text-xs px-1 py-0.5 rounded truncate`}
                      title={event.event}
                    >
                      {event.event}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen">
      <StartSidebar />
      <div className="p-6">
        <div className="w-full max-w-full">
          {/* Exit Assessment Button */}
          <div className="mb-6 flex justify-end">
            <button
              onClick={() => navigate('/start/dashboard')}
              className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors"
            >
              <FaArrowLeft className="h-4 w-4" />
              Back to Dashboard
            </button>
          </div>

          {/* Title and Subtitle - Show for Step 1 */}
          {stepperStep === 1 && (
            <div className="text-center mb-8">
              <h3 className="text-5xl font-semibold text-gray-900 dark:text-white mb-4">
                {companyName ? `${companyName} Snapshot` : 'Company Snapshot'}
              </h3>
              <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
                Tell us about yourself and your company.
              </p>
            </div>
          )}

          {/* Title and Subtitle - Show for Step 2 */}
          {stepperStep === 2 && (
            <div className="text-center mb-8">
              <h3 className="text-5xl font-semibold text-gray-900 dark:text-white mb-4">
                Learn about the BBA Team & Process
              </h3>
              <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
                Get to know our team and how we work.
              </p>
            </div>
          )}

          {/* Title and Subtitle - Show for Step 3 */}
          {stepperStep === 3 && (
            <div className="text-center mb-8">
              <h3 className="text-5xl font-semibold text-gray-900 dark:text-white mb-4">
                Establish Your Why
              </h3>
              <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
                Define your mission, vision, and values.
              </p>
            </div>
          )}

          {/* Title and Subtitle - Show for Step 4 */}
          {stepperStep === 4 && (
            <div className="text-center mb-8">
              <h3 className="text-5xl font-semibold text-gray-900 dark:text-white mb-4">
                Proposed Conditions
              </h3>
              <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
                Define your desired future state.
              </p>
            </div>
          )}

          {/* Title - Show for Step 5 */}
          {stepperStep === 5 && (
            <div className="text-center mb-8">
              <h3 className="text-5xl font-semibold text-gray-900 dark:text-white mb-4">
                Program Overview
              </h3>
            </div>
          )}

          {/* Title - Show for Step 6 */}
          {stepperStep === 6 && (
            <div className="text-center mb-8">
              <h3 className="text-5xl font-semibold text-gray-900 dark:text-white mb-4">
                Program Overview
              </h3>
            </div>
          )}

          {/* Step Indicators */}
          <div className="flex justify-center items-center gap-4 mb-8">
            {/* Left Arrow */}
            <button
              onClick={goToPreviousStep}
              disabled={stepperStep === 1}
              className={`flex h-10 w-10 items-center justify-center rounded-full font-semibold transition-colors ${
                stepperStep === 1
                  ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed opacity-50'
                  : 'bg-gray-500 dark:bg-gray-600 text-white hover:bg-gray-600 dark:hover:bg-gray-500 cursor-pointer'
              }`}
              aria-label="Previous step"
            >
              <FaArrowLeft className="h-5 w-5" />
            </button>

            {[1, 2, 3, 4, 5, 6, 7].map((stepNum) => {
              const canAccess = isStepAccessible(stepNum) || isStepCompleted(stepNum);
              return (
                <React.Fragment key={stepNum}>
                  <div
                    onClick={() => {
                      if (canAccess) {
                        setStepperStep(stepNum);
                      }
                    }}
                    className={`relative cursor-pointer outline-none focus:outline-none ${
                      !canAccess ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <div
                      className={`flex h-10 w-10 items-center justify-center rounded-full font-semibold transition-colors ${
                        stepperStep === stepNum
                          ? 'bg-red-600 text-white'
                          : stepperStep > stepNum
                            ? 'bg-green-600 text-white'
                            : 'bg-gray-400 text-white'
                      }`}
                    >
                      {stepperStep > stepNum ? (
                        <FaCheck className="h-5 w-5 text-white" />
                      ) : (
                        <span className="text-sm text-white">{stepNum}</span>
                      )}
                    </div>
                  </div>
                  {stepNum < 7 && (
                    <div className={`h-1 w-12 ${stepperStep > stepNum ? 'bg-green-600' : 'bg-gray-400'}`} />
                  )}
                </React.Fragment>
              );
            })}

            {/* Right Arrow */}
            <button
              onClick={goToNextStep}
              disabled={stepperStep === 7}
              className={`flex h-10 w-10 items-center justify-center rounded-full font-semibold transition-colors ${
                stepperStep === 7
                  ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed opacity-50'
                  : 'bg-gray-500 dark:bg-gray-600 text-white hover:bg-gray-600 dark:hover:bg-gray-500 cursor-pointer'
              }`}
              aria-label="Next step"
            >
              <FaArrowRight className="h-5 w-5" />
            </button>
          </div>

          {/* Step Content */}
          <div className="w-full min-h-[500px]">
            {/* Step 1: Company Snapshot */}
            {stepperStep === 1 && (
              <div className="rounded-xl p-8 md:p-12">
                {/* Four Icons with Headers */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8 w-full">
                  {/* Company Background */}
                  <div className="bg-white/20 dark:bg-white/10 backdrop-blur-md rounded-xl p-6 flex flex-col shadow-lg border border-white/30 dark:border-white/20">
                    <div className="mb-4 flex justify-center">
                      <FaBuilding className="text-5xl text-red-600 dark:text-red-400" />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 text-center">Company Background</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 text-center">
                      Your background and the company's background
                    </p>
                    <div className="space-y-4 w-full">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Years in business
                        </label>
                        <input
                          type="number"
                          value={companyYears}
                          onChange={(e) => setCompanyYears(e.target.value)}
                          placeholder="Enter years"
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-600 dark:focus:ring-red-400"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Team size
                        </label>
                        <input
                          type="number"
                          value={teamSize}
                          onChange={(e) => setTeamSize(e.target.value)}
                          placeholder="Enter team size"
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-600 dark:focus:ring-red-400"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Project Profile */}
                  <div className="bg-white/20 dark:bg-white/10 backdrop-blur-md rounded-xl p-6 flex flex-col shadow-lg border border-white/30 dark:border-white/20">
                    <div className="mb-4 flex justify-center">
                      <FaProjectDiagram className="text-5xl text-red-600 dark:text-red-400" />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 text-center">Project Profile</h4>
                    <div className="space-y-4 w-full">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Primary Projects
                        </label>
                        <input
                          type="text"
                          value={primaryProjects}
                          onChange={(e) => setPrimaryProjects(e.target.value)}
                          placeholder="Enter primary projects"
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-600 dark:focus:ring-red-400"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Average Size
                        </label>
                        <input
                          type="text"
                          value={averageSize}
                          onChange={(e) => setAverageSize(e.target.value)}
                          placeholder="Enter average size"
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-600 dark:focus:ring-red-400"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Project Type
                        </label>
                        <div className="flex flex-col gap-2">
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name="projectType"
                              value="Remodels"
                              checked={projectType === 'Remodels'}
                              onChange={(e) => setProjectType(e.target.value)}
                              className="mr-2 text-red-600 focus:ring-red-600"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">Remodels</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name="projectType"
                              value="New Construction"
                              checked={projectType === 'New Construction'}
                              onChange={(e) => setProjectType(e.target.value)}
                              className="mr-2 text-red-600 focus:ring-red-600"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">New Construction</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name="projectType"
                              value="Both"
                              checked={projectType === 'Both'}
                              onChange={(e) => setProjectType(e.target.value)}
                              className="mr-2 text-red-600 focus:ring-red-600"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">Both</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Market */}
                  <div className="bg-white/20 dark:bg-white/10 backdrop-blur-md rounded-xl p-6 flex flex-col items-center shadow-lg border border-white/30 dark:border-white/20">
                    <div className="mb-4">
                      <FaGlobe className="text-5xl text-red-600 dark:text-red-400" />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Market</h4>
                    <ul className="text-left text-gray-800 dark:text-gray-200 space-y-2">
                      <li>• Primary location(s)</li>
                      <li>• Typical client type</li>
                      <li>• Competitive intensity</li>
                      <li>• Lead sources</li>
                    </ul>
                  </div>
                  
                  {/* Strengths Weaknesses */}
                  <div className="bg-white/20 dark:bg-white/10 backdrop-blur-md rounded-xl p-6 flex flex-col items-center shadow-lg border border-white/30 dark:border-white/20">
                    <div className="mb-4">
                      <FaBalanceScale className="text-5xl text-red-600 dark:text-red-400" />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Strengths / Weaknesses</h4>
                    <ul className="text-left text-gray-800 dark:text-gray-200 space-y-2">
                      <li>• What's working well</li>
                      <li>• Where things break</li>
                      <li>• Biggest advantage</li>
                      <li>• Biggest constraint</li>
                    </ul>
                  </div>
                </div>
                
                {!isStepCompleted(1) && (
                  <div className="text-center">
                    <button
                      onClick={() => {
                        if (isStepAccessible(1)) {
                          navigate('/start/discovery');
                        }
                      }}
                      disabled={!isStepAccessible(1)}
                      className={`w-full max-w-md mx-auto px-8 py-4 text-lg rounded-lg font-semibold transition-colors ${
                        isStepAccessible(1)
                          ? 'bg-red-600 text-white hover:bg-red-700'
                          : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                      }`}
                    >
                      Start Company Snapshot
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Learn about the BBA Team & Process */}
            {stepperStep === 2 && (
              <div className="py-8 w-full">
                {/* Department Profile Cards */}
                <div className="mb-8 flex justify-center">
                  <div className="max-w-5xl w-full px-4">
                    <div className="grid grid-cols-3 gap-6">
                      {/* Sales Department - Red */}
                      <TeamMemberCard
                        name="SALES"
                        fullName="Ken Combs"
                        avatarUrl="/kenshot.png"
                        borderColor="border-red-500/30"
                        services={['Referral Programs', 'Closing Sales', 'Sales Process']}
                      />

                      {/* Operations Department - Blue */}
                      <TeamMemberCard
                        name="OPERATIONS"
                        fullName="Daryl Mejeur"
                        avatarUrl="/darylshot.png"
                        borderColor="border-blue-500/30"
                        services={['Process Optimization', 'Workflow Management', 'Efficiency Systems']}
                      />

                      {/* Finance Department - Green */}
                      <TeamMemberCard
                        name="FINANCE"
                        fullName="Ian Horowitz"
                        avatarUrl="/ianshot.png"
                        borderColor="border-green-500/30"
                        services={['Financial Planning', 'Budget Management', 'Growth Strategies']}
                      />

                      {/* Technology Department - Purple */}
                      <TeamMemberCard
                        name="TECHNOLOGY"
                        fullName="Ian Riddle"
                        avatarUrl="/riddleshot.png"
                        borderColor="border-purple-500/30"
                        services={['System Integration', 'Automation Solutions', 'Tech Infrastructure']}
                      />

                      {/* Marketing Department - Orange */}
                      <TeamMemberCard
                        name="MARKETING"
                        fullName="Parker Sexton"
                        avatarUrl="/parker.png"
                        borderColor="border-orange-500/30"
                        services={['Brand Development', 'Digital Marketing', 'Content Strategy']}
                      />

                      {/* HR Department - Yellow */}
                      <TeamMemberCard
                        name="HR"
                        fullName="Annette Rushin"
                        avatarUrl="/annshot.png"
                        borderColor="border-yellow-500/30"
                        services={['Hiring Service', 'Employee Handbook', 'Legal Materials']}
                      />
                    </div>
                  </div>
                </div>

                {/* Our Background Section */}
                <div className="mt-12 text-center">
                  <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">Our Background</h3>
                  <div className="flex justify-center items-center gap-8">
                    <img 
                      src="/cqclogo.png" 
                      alt="CQC Logo" 
                      className="h-16 md:h-20 object-contain"
                    />
                    <img 
                      src="/greenlogo.png" 
                      alt="Green Logo" 
                      className="h-16 md:h-20 object-contain"
                    />
                    <img 
                      src="/artlogo.png" 
                      alt="Art Logo" 
                      className="h-16 md:h-20 object-contain"
                    />
                  </div>
                </div>
                
                {!isStepCompleted(2) && (
                  <div className="text-center mt-8">
                    <button
                      onClick={() => {
                        if (isStepAccessible(2)) {
                          navigate('/start/team-process');
                        }
                      }}
                      disabled={!isStepAccessible(2)}
                      className={`w-full max-w-md mx-auto px-8 py-4 text-lg rounded-lg font-semibold transition-colors ${
                        isStepAccessible(2)
                          ? 'bg-red-600 text-white hover:bg-red-700'
                          : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                      }`}
                    >
                      Learn More
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Establish Your Why */}
            {stepperStep === 3 && (
              <div className="py-8">
                <div className="max-w-3xl mx-auto space-y-6">
                  {/* Why Input */}
                  <div>
                    <label className="block text-lg font-semibold text-gray-900 dark:text-white mb-3">
                      Your Why
                    </label>
                    <textarea
                      placeholder="What drives you? What is your purpose?"
                      rows={4}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-600 dark:focus:ring-red-400"
                    />
                  </div>

                  {/* Goals */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Goals</h4>
                    
                    {/* Goal 1 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Goal 1
                      </label>
                      <input
                        type="text"
                        placeholder="Enter your first goal"
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-600 dark:focus:ring-red-400"
                      />
                    </div>

                    {/* Goal 2 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Goal 2
                      </label>
                      <input
                        type="text"
                        placeholder="Enter your second goal"
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-600 dark:focus:ring-red-400"
                      />
                    </div>

                    {/* Goal 3 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Goal 3
                      </label>
                      <input
                        type="text"
                        placeholder="Enter your third goal"
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-600 dark:focus:ring-red-400"
                      />
                    </div>
                  </div>

                  {!isStepCompleted(3) && (
                    <div className="text-center pt-4">
                      <button
                        onClick={() => {
                          if (isStepAccessible(3)) {
                            navigate('/start/why');
                          }
                        }}
                        disabled={!isStepAccessible(3)}
                        className={`w-full max-w-md mx-auto px-8 py-4 text-lg rounded-lg font-semibold transition-colors ${
                          isStepAccessible(3)
                            ? 'bg-red-600 text-white hover:bg-red-700'
                            : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                        }`}
                      >
                        Establish Your Why
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 4: Proposed Conditions */}
            {stepperStep === 4 && (
              <div className="py-8">
                <div className="max-w-2xl mx-auto">
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Target Revenue
                      </label>
                      <input
                        type="text"
                        placeholder="Enter target revenue"
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-600 dark:focus:ring-red-400"
                      />
                    </div>
                  </div>

                  {!isStepCompleted(4) && (
                    <div className="text-center pt-8">
                      <button
                        onClick={() => {
                          if (isStepAccessible(4)) {
                            navigate('/start/existing-conditions');
                          }
                        }}
                        disabled={!isStepAccessible(4)}
                        className={`w-full max-w-md mx-auto px-8 py-4 text-lg rounded-lg font-semibold transition-colors ${
                          isStepAccessible(4)
                            ? 'bg-red-600 text-white hover:bg-red-700'
                            : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                        }`}
                      >
                        Continue Assessment
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 5: Program Overview */}
            {stepperStep === 5 && (
              <div className="py-8">
                {isLoadingPrograms ? (
                  <div className="text-center py-12">
                    <div className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                      <BBALoader size={16} className="mr-2" />
                      Loading programs...
                    </div>
                  </div>
                ) : (
                  <div className="max-w-7xl mx-auto">
                    {/* Billing Toggle */}
                    <div className="flex justify-center mb-8">
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-1 shadow-sm border border-gray-200 dark:border-gray-700">
                        <div className="flex">
                          {['monthly', 'quarterly', 'annual'].map((cycleKey) => {
                            const cycle = billingCycles[cycleKey];
                            if (!cycle) return null;
                            
                            return (
                              <button
                                key={cycleKey}
                                onClick={() => setBillingCycle(cycleKey)}
                                className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                                  billingCycle === cycleKey
                                    ? 'bg-red-600 text-white'
                                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                                }`}
                              >
                                {cycle.name}
                                {cycle.discount > 0 && (
                                  <span className="ml-1 text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-full">
                                    Save {cycle.discount}%
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Program Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
                      {getOrderedPrograms().map(([programKey, program]) => {
                        const validatedProgram = validateProgram(program);
                        const isPlusProgram = programKey.toLowerCase().includes('plus') || 
                                             (validatedProgram.name && validatedProgram.name.toLowerCase().includes('plus'));
                        
                        return (
                          <div
                            key={programKey}
                            className={`relative bg-white dark:bg-gray-800 rounded-xl shadow-lg border-2 transition-all duration-200 hover:shadow-xl h-full flex flex-col ${
                              isPlusProgram
                                ? 'border-red-500'
                                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                            }`}
                          >
                            {validatedProgram.isMostPopular && (
                              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                                <span className="bg-red-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                                  Most Popular
                                </span>
                              </div>
                            )}
                            
                            {/* Suggested Star - Top Right */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSuggestedProgram(suggestedProgram === programKey ? null : programKey);
                              }}
                              className="absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center transition-all hover:scale-110 z-10"
                              title={suggestedProgram === programKey ? 'Remove suggestion' : 'Mark as suggested'}
                            >
                              <div className={`w-full h-full rounded-full flex items-center justify-center ${
                                suggestedProgram === programKey
                                  ? 'bg-yellow-400'
                                  : 'bg-gray-200 dark:bg-gray-700'
                              }`}>
                                <FaStar 
                                  className={`${
                                    suggestedProgram === programKey
                                      ? 'text-yellow-900 fill-current'
                                      : 'text-gray-400 dark:text-gray-500'
                                  }`}
                                  size={16}
                                />
                              </div>
                            </button>
                            
                            <div className="p-6 flex flex-col h-full">
                              <div className="text-center mb-4">
                                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{validatedProgram.name}</h3>
                                <p className="text-gray-600 dark:text-gray-300 text-sm">{validatedProgram.description}</p>
                              </div>

                              <div className="text-center mb-4">
                                {billingCycle === 'monthly' && (
                                  <div className="flex items-baseline justify-center">
                                    <span className="text-3xl font-bold text-gray-900 dark:text-white">$</span>
                                    <span className="text-4xl font-bold text-gray-900 dark:text-white ml-1">
                                      {validatedProgram.pricePerMonth}
                                    </span>
                                    <span className="text-gray-600 dark:text-gray-300 ml-2">/month</span>
                                  </div>
                                )}
                                {billingCycle === 'quarterly' && billingCycles?.quarterly && (
                                  <>
                                    <div className="flex items-baseline justify-center">
                                      <span className="text-3xl font-bold text-gray-900 dark:text-white">$</span>
                                      <span className="text-4xl font-bold text-gray-900 dark:text-white ml-1">
                                        {getQuarterlyPrice(validatedProgram.pricePerMonth)}
                                      </span>
                                      <span className="text-gray-600 dark:text-gray-300 ml-2">/quarter</span>
                                    </div>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                      ${getMonthlyPrice(validatedProgram.pricePerMonth)}/month
                                    </p>
                                  </>
                                )}
                                {billingCycle === 'annual' && billingCycles?.annual && (
                                  <>
                                    <div className="flex items-baseline justify-center">
                                      <span className="text-3xl font-bold text-gray-900 dark:text-white">$</span>
                                      <span className="text-4xl font-bold text-gray-900 dark:text-white ml-1">
                                        {getMonthlyPrice(validatedProgram.pricePerMonth)}
                                      </span>
                                      <span className="text-gray-600 dark:text-gray-300 ml-2">/month</span>
                                    </div>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                      ${getAnnualPrice(validatedProgram.pricePerMonth)}/year
                                    </p>
                                  </>
                                )}
                              </div>

                              {/* Showcase Features */}
                              {validatedProgram.features && Object.entries(validatedProgram.features).filter(([_, featureData]) => featureData.showcase).length > 0 && (
                                <div className="space-y-2 mb-4 flex-1">
                                  {getOrderedShowcaseFeatures(programKey).map((feature) => {
                                    const featureData = validatedProgram.features[feature];
                                    const isCheckbox = featureData.quantity === '0';
                                    // Find the item in availableItems to get its icon
                                    const item = availableItems.find(availItem => availItem.name === feature);
                                    const itemIcon = item?.icon || '📦';
                                    
                                    return (
                                      <div key={feature} className="flex items-center justify-between p-2 rounded-md border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50">
                                        <div className="flex items-center flex-1">
                                          <span className="text-lg mr-2">{itemIcon}</span>
                                          <span className="text-sm text-gray-700 dark:text-gray-300">{feature}</span>
                                        </div>
                                        <span className="text-sm font-medium text-gray-900 dark:text-white ml-2">
                                          {isCheckbox ? (
                                            <FaCheck className="text-green-500 text-xs" />
                                          ) : (
                                            featureData.quantity
                                          )}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}

                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Program Filter Buttons */}
                    <div className="flex justify-center gap-4 mb-6 mt-8">
                      {['Lite', 'Plus', 'Pro', 'Bundle'].map((filter) => (
                        <button
                          key={filter}
                          onClick={() => setSelectedProgramFilter(filter)}
                          className={`px-6 py-2 rounded-lg font-semibold transition-colors ${
                            selectedProgramFilter === filter
                              ? 'bg-red-600 text-white'
                              : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                          }`}
                        >
                          {filter === 'Bundle' ? 'BBA Bundle' : filter}
                        </button>
                      ))}
                    </div>

                    {/* Legend */}
                    <div className="flex justify-center items-center gap-4 mb-6">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-red-600 rounded"></div>
                        <span className="text-sm text-gray-700 dark:text-gray-300">1on1 Call</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-green-600 rounded"></div>
                        <span className="text-sm text-gray-700 dark:text-gray-300">Group Call</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-yellow-400 rounded"></div>
                        <span className="text-sm text-gray-700 dark:text-gray-300">Gold In Person visit</span>
                      </div>
                    </div>

                    {/* Calendar */}
                    {renderCalendar()}
                  </div>
                )}
              </div>
            )}

            {/* Step 6: Program Overview */}
            {stepperStep === 6 && (
              <div className="py-8">
                {isLoadingPrograms ? (
                  <div className="text-center py-12">
                    <div className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                      <BBALoader size={16} className="mr-2" />
                      Loading programs...
                    </div>
                  </div>
                ) : (
                  <div className="max-w-7xl mx-auto">
                    {/* Billing Toggle */}
                    <div className="flex justify-center mb-8">
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-1 shadow-sm border border-gray-200 dark:border-gray-700">
                        <div className="flex">
                          {['monthly', 'quarterly', 'annual'].map((cycleKey) => {
                            const cycle = billingCycles[cycleKey];
                            if (!cycle) return null;
                            
                            return (
                              <button
                                key={cycleKey}
                                onClick={() => setBillingCycle(cycleKey)}
                                className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                                  billingCycle === cycleKey
                                    ? 'bg-red-600 text-white'
                                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                                }`}
                              >
                                {cycle.name}
                                {cycle.discount > 0 && (
                                  <span className="ml-1 text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-full">
                                    Save {cycle.discount}%
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Program Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
                      {getOrderedPrograms().map(([programKey, program]) => {
                        const validatedProgram = validateProgram(program);
                        const isPlusProgram = programKey.toLowerCase().includes('plus') || 
                                             (validatedProgram.name && validatedProgram.name.toLowerCase().includes('plus'));
                        
                        return (
                          <div
                            key={programKey}
                            className={`relative bg-white dark:bg-gray-800 rounded-xl shadow-lg border-2 transition-all duration-200 hover:shadow-xl h-full flex flex-col ${
                              isPlusProgram
                                ? 'border-red-500'
                                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                            }`}
                          >
                            {validatedProgram.isMostPopular && (
                              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                                <span className="bg-red-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                                  Most Popular
                                </span>
                              </div>
                            )}
                            
                            {/* Suggested Star - Top Right */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSuggestedProgram(suggestedProgram === programKey ? null : programKey);
                              }}
                              className="absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center transition-all hover:scale-110 z-10"
                              title={suggestedProgram === programKey ? 'Remove suggestion' : 'Mark as suggested'}
                            >
                              <div className={`w-full h-full rounded-full flex items-center justify-center ${
                                suggestedProgram === programKey
                                  ? 'bg-yellow-400'
                                  : 'bg-gray-200 dark:bg-gray-700'
                              }`}>
                                <FaStar 
                                  className={`${
                                    suggestedProgram === programKey
                                      ? 'text-yellow-900 fill-current'
                                      : 'text-gray-400 dark:text-gray-500'
                                  }`}
                                  size={16}
                                />
                              </div>
                            </button>
                            
                            <div className="p-6 flex flex-col h-full">
                              <div className="text-center mb-4">
                                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{validatedProgram.name}</h3>
                                <p className="text-gray-600 dark:text-gray-300 text-sm">{validatedProgram.description}</p>
                              </div>

                              <div className="text-center mb-4">
                                {billingCycle === 'monthly' && (
                                  <div className="flex items-baseline justify-center">
                                    <span className="text-3xl font-bold text-gray-900 dark:text-white">$</span>
                                    <span className="text-4xl font-bold text-gray-900 dark:text-white ml-1">
                                      {validatedProgram.pricePerMonth}
                                    </span>
                                    <span className="text-gray-600 dark:text-gray-300 ml-2">/month</span>
                                  </div>
                                )}
                                {billingCycle === 'quarterly' && billingCycles?.quarterly && (
                                  <>
                                    <div className="flex items-baseline justify-center">
                                      <span className="text-3xl font-bold text-gray-900 dark:text-white">$</span>
                                      <span className="text-4xl font-bold text-gray-900 dark:text-white ml-1">
                                        {getQuarterlyPrice(validatedProgram.pricePerMonth)}
                                      </span>
                                      <span className="text-gray-600 dark:text-gray-300 ml-2">/quarter</span>
                                    </div>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                      ${getMonthlyPrice(validatedProgram.pricePerMonth)}/month
                                    </p>
                                  </>
                                )}
                                {billingCycle === 'annual' && billingCycles?.annual && (
                                  <>
                                    <div className="flex items-baseline justify-center">
                                      <span className="text-3xl font-bold text-gray-900 dark:text-white">$</span>
                                      <span className="text-4xl font-bold text-gray-900 dark:text-white ml-1">
                                        {getMonthlyPrice(validatedProgram.pricePerMonth)}
                                      </span>
                                      <span className="text-gray-600 dark:text-gray-300 ml-2">/month</span>
                                    </div>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                      ${getAnnualPrice(validatedProgram.pricePerMonth)}/year
                                    </p>
                                  </>
                                )}
                              </div>

                              {/* Showcase Features */}
                              {validatedProgram.features && Object.entries(validatedProgram.features).filter(([_, featureData]) => featureData.showcase).length > 0 && (
                                <div className="space-y-2 mb-4 flex-1">
                                  {getOrderedShowcaseFeatures(programKey).map((feature) => {
                                    const featureData = validatedProgram.features[feature];
                                    const isCheckbox = featureData.quantity === '0';
                                    // Find the item in availableItems to get its icon
                                    const item = availableItems.find(availItem => availItem.name === feature);
                                    const itemIcon = item?.icon || '📦';
                                    
                                    return (
                                      <div key={feature} className="flex items-center justify-between p-2 rounded-md border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50">
                                        <div className="flex items-center flex-1">
                                          <span className="text-lg mr-2">{itemIcon}</span>
                                          <span className="text-sm text-gray-700 dark:text-gray-300">{feature}</span>
                                        </div>
                                        <span className="text-sm font-medium text-gray-900 dark:text-white ml-2">
                                          {isCheckbox ? (
                                            <FaCheck className="text-green-500 text-xs" />
                                          ) : (
                                            featureData.quantity
                                          )}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}

                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Program Filter Buttons */}
                    <div className="flex justify-center gap-4 mb-6 mt-8">
                      {['Lite', 'Plus', 'Pro', 'Bundle'].map((filter) => (
                        <button
                          key={filter}
                          onClick={() => setSelectedProgramFilter(filter)}
                          className={`px-6 py-2 rounded-lg font-semibold transition-colors ${
                            selectedProgramFilter === filter
                              ? 'bg-red-600 text-white'
                              : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                          }`}
                        >
                          {filter === 'Bundle' ? 'BBA Bundle' : filter}
                        </button>
                      ))}
                    </div>

                    {/* Legend */}
                    <div className="flex justify-center items-center gap-4 mb-6">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-red-600 rounded"></div>
                        <span className="text-sm text-gray-700 dark:text-gray-300">1on1 Call</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-green-600 rounded"></div>
                        <span className="text-sm text-gray-700 dark:text-gray-300">Group Call</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-yellow-400 rounded"></div>
                        <span className="text-sm text-gray-700 dark:text-gray-300">Gold In Person visit</span>
                      </div>
                    </div>

                    {/* Calendar */}
                    {renderCalendar()}
                  </div>
                )}
              </div>
            )}

            {/* Step 7: Sign Up */}
            {stepperStep === 7 && (
              <div className="py-8 text-center">
                <h3 className="text-3xl font-semibold text-gray-900 dark:text-white mb-4">Sign Up</h3>
                <p className="text-xl text-gray-600 dark:text-gray-300 mb-6">
                  Complete your registration and sign your contract.
                </p>
                {!isStepCompleted(7) && (
                  <button
                    onClick={() => {
                      if (isStepAccessible(7)) {
                        navigate('/start/checkout');
                      }
                    }}
                    disabled={!isStepAccessible(7)}
                    className={`w-full max-w-md mx-auto px-8 py-4 text-lg rounded-lg font-semibold transition-colors ${
                      isStepAccessible(7)
                        ? 'bg-red-600 text-white hover:bg-red-700'
                        : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                    }`}
                  >
                    Complete Sign Up
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StartAssessment;

